import React, { useEffect, useState } from 'react';
import { authService } from '../services/authService';
import { logUiEvent } from '../services/uiLogger';
import { OFFER_VERSION, CONSENT_VERSION } from '../config/legalVersions';

// Окно «Обновились документы»: человек без акцепта текущих редакций оферты и
// согласия соглашается или выходит (спека 2026-10-10-legal-acceptance-design.md).
// Сбой bit-auth окна не показывает — запирать продукт из-за него нельзя.
const RELOAD_FLAG = 'legal-reloaded';

export default function LegalGate({ onLogout }) {
  const [required, setRequired] = useState(false);
  const [tick, setTick] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    authService.getProfile()
      .then((profile) => { if (alive) setRequired(!!profile?.legal?.required); })
      .catch(() => logUiEvent('legal_gate_error', { component: 'legal', meta: { step: 'profile' } }));
    return () => { alive = false; };
  }, []);

  if (!required) return null;

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      await authService.acceptLegal(OFFER_VERSION, CONSENT_VERSION);
      // Флаг снимаем: иначе следующее настоящее 409 в этой вкладке не даст перезагрузку.
      try { sessionStorage.removeItem(RELOAD_FLAG); } catch (_) { /* приватный режим */ }
      setRequired(false);
    } catch (e) {
      if (e.code !== 'legal_version_mismatch') {
        setError(e.message);
        return;
      }
      // Бандл показал старую редакцию. Перезагрузка подтянет новый — но одна:
      // кэш или bit-auth, поднявшийся позже фронта, иначе дали бы петлю.
      let reloaded = false;
      try { reloaded = !!sessionStorage.getItem(RELOAD_FLAG); } catch (_) { /* приватный режим */ }
      if (!reloaded) {
        try { sessionStorage.setItem(RELOAD_FLAG, '1'); } catch (_) { /* приватный режим */ }
        window.location.reload();
        return;
      }
      setError('Документы обновились. Обновите страницу.');
    } finally {
      setBusy(false);
    }
  };

  const link = 'font-semibold text-paper-accent hover:underline';
  return (
    <div data-testid="legal-gate" role="dialog" aria-modal="true" aria-labelledby="legal-gate-title"
         className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-panel bg-paper-card p-6 space-y-4 shadow-xl">
        <h2 id="legal-gate-title" className="text-lg font-bold text-paper-ink">Обновились документы</h2>
        <p className="text-sm text-paper-ink-2">
          Чтобы продолжить работу, ознакомьтесь с{' '}
          <a href="/oferta" target="_blank" rel="noreferrer" className={link}>офертой</a>,{' '}
          <a href="/consent" target="_blank" rel="noreferrer" className={link}>согласием на обработку персональных данных</a>{' '}
          и <a href="/privacy" target="_blank" rel="noreferrer" className={link}>политикой конфиденциальности</a>.
        </p>
        <label className="flex items-start gap-3 text-sm text-paper-ink-2 cursor-pointer">
          <input type="checkbox" checked={tick} onChange={(e) => setTick(e.target.checked)}
                 className="mt-[2px] accent-paper-accent w-4 h-4" />
          <span>Я соглашаюсь с офертой и согласием на обработку персональных данных</span>
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onLogout}
                  className="px-4 py-2 rounded-panel text-sm text-paper-ink-2 hover:bg-paper-panel">Выйти</button>
          <button type="button" onClick={accept} disabled={!tick || busy}
                  className="px-4 py-2 rounded-panel text-sm font-bold text-white bg-paper-accent disabled:opacity-50">
            Принять
          </button>
        </div>
      </div>
    </div>
  );
}
